/**
 * Hook motion: the entrance / exit / hold animation model for hook takeovers.
 *
 * A hook's motion is two PHASES (entrance + exit), each with its own kind,
 * duration, travel distance, motion blur and easing, plus:
 *  - `split`: what animates as a unit - the whole block, each line, each word
 *    or each letter - with a `staggerMs` cascade between units;
 *  - `idle`: a subtle "hold" motion while the hook is fully on screen.
 *
 * Everything is a pure function of (hook, time), so the browser preview and the
 * export renderer evaluate identical frames.
 */
import { clamp01 } from './animation.js';
export const STILL = {
    opacity: 1,
    tx: 0,
    ty: 0,
    scale: 1,
    rotateDeg: 0,
    blurPx: 0,
    maskOffset: 0,
    trackEm: 0,
    masked: false,
};
/** Easing curves (a superset of the caption engine's, hook-local). */
export function hookEase(t, kind) {
    const x = clamp01(t);
    switch (kind) {
        case 'linear':
            return x;
        case 'easeInOut':
            return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
        case 'expo':
            return x >= 1 ? 1 : 1 - Math.pow(2, -10 * x);
        case 'back': {
            const c1 = 1.70158;
            const c3 = c1 + 1;
            return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
        }
        case 'spring': {
            if (x <= 0)
                return 0;
            if (x >= 1)
                return 1;
            const decay = Math.exp(-6 * x);
            return 1 - Math.pow(1 - x, 2) + Math.sin(x * Math.PI * 2.2) * decay * 0.35;
        }
        case 'bounce': {
            const n1 = 7.5625;
            const d1 = 2.75;
            let v = x;
            if (v < 1 / d1)
                return n1 * v * v;
            if (v < 2 / d1)
                return n1 * (v -= 1.5 / d1) * v + 0.75;
            if (v < 2.5 / d1)
                return n1 * (v -= 2.25 / d1) * v + 0.9375;
            return n1 * (v -= 2.625 / d1) * v + 0.984375;
        }
        case 'easeOut':
        default:
            return 1 - Math.pow(1 - x, 3);
    }
}
/** Sensible per-kind defaults (distance / blur / easing / fade). */
export const MOTION_KIND_DEFAULTS = {
    none: { distance: 0, blur: 0, easing: 'linear', fade: false },
    fade: { distance: 0, blur: 0, easing: 'easeOut', fade: true },
    slideUp: { distance: 0.1, blur: 0.2, easing: 'expo', fade: true },
    slideDown: { distance: 0.1, blur: 0.2, easing: 'expo', fade: true },
    slideLeft: { distance: 0.25, blur: 0.25, easing: 'expo', fade: true },
    slideRight: { distance: 0.25, blur: 0.25, easing: 'expo', fade: true },
    zoomIn: { distance: 0.28, blur: 0, easing: 'easeOut', fade: true },
    zoomOut: { distance: 0.3, blur: 0.3, easing: 'expo', fade: true },
    punch: { distance: 0.4, blur: 0, easing: 'spring', fade: true },
    shake: { distance: 0.3, blur: 0, easing: 'spring', fade: true },
    blur: { distance: 0.3, blur: 0.8, easing: 'easeOut', fade: true },
    mask: { distance: 0, blur: 0, easing: 'expo', fade: false },
    drop: { distance: 0.2, blur: 0, easing: 'bounce', fade: true },
    spin: { distance: 0.25, blur: 0.2, easing: 'back', fade: true },
    trackIn: { distance: 0.5, blur: 0.15, easing: 'expo', fade: true },
};
const OUT_FADE = { kind: 'fade', durationMs: 260, distance: 0, blur: 0, easing: 'easeOut', fade: true };
/** The motion implied by the legacy single-choice `animation` field. */
export function legacyMotion(animation) {
    const base = { out: OUT_FADE, split: 'block', staggerMs: 0, idle: 'none', idleAmount: 0.5 };
    switch (animation) {
        case 'zoomIn':
            return { ...base, in: { kind: 'zoomIn', durationMs: 300, distance: 0.28, blur: 0, easing: 'easeOut', fade: true } };
        case 'popShake':
            return { ...base, in: { kind: 'shake', durationMs: 300, distance: 0.3, blur: 0, easing: 'spring', fade: true } };
        case 'riseFade':
            return { ...base, in: { kind: 'slideUp', durationMs: 300, distance: 0.05, blur: 0, easing: 'easeOut', fade: true } };
        case 'none':
            return { ...base, in: { kind: 'fade', durationMs: 80, distance: 0, blur: 0, easing: 'linear', fade: true } };
        case 'punch':
        default:
            return { ...base, in: { kind: 'punch', durationMs: 300, distance: 0.4, blur: 0, easing: 'spring', fade: true } };
    }
}
/** The effective motion for a hook: its own `motion`, else the legacy mapping. */
export function resolveHookMotion(h) {
    return h.motion ?? legacyMotion(h.animation);
}
/**
 * State contributed by ONE phase. `v` is the phase's visibility 0 (hidden) → 1
 * (fully shown): for an entrance it is the eased progress, for an exit the eased
 * remaining fraction. `exit` flips slide / mask / drop / spin directions so an
 * exit keeps travelling (a slide-up exit leaves upward).
 */
function phaseState(p, v, exit, env) {
    const e = hookEase(v, p.easing);
    const inv = 1 - e;
    const sign = exit ? -1 : 1;
    const d = p.distance;
    const fade = p.fade ?? MOTION_KIND_DEFAULTS[p.kind].fade ?? true;
    const s = { ...STILL };
    if (p.kind === 'none') {
        s.opacity = v > 0 ? 1 : 0;
        return s;
    }
    // Opacity follows a non-overshooting curve so a spring/back never "blinks".
    s.opacity = fade ? clamp01(hookEase(v, p.easing === 'spring' || p.easing === 'back' || p.easing === 'bounce' ? 'easeOut' : p.easing)) : v > 0 ? 1 : 0;
    switch (p.kind) {
        case 'slideUp':
            s.ty = inv * d * env.height * sign;
            break;
        case 'slideDown':
            s.ty = -inv * d * env.height * sign;
            break;
        case 'slideLeft':
            s.tx = inv * d * env.width * sign;
            break;
        case 'slideRight':
            s.tx = -inv * d * env.width * sign;
            break;
        case 'zoomIn':
        case 'punch':
            s.scale = 1 - inv * d;
            break;
        case 'shake': {
            s.scale = 1 - inv * d;
            // A decaying wiggle over ~1.5x the phase (entrance only).
            const span = Math.max(1, p.durationMs * 1.5);
            if (!exit && env.localMs < span) {
                const decay = 1 - env.localMs / span;
                s.rotateDeg = Math.sin(env.localMs / 45) * 3.2 * (d / 0.3) * decay;
            }
            break;
        }
        case 'zoomOut':
            s.scale = 1 + inv * d * 2;
            break;
        case 'blur':
            s.scale = 1 + inv * 0.08 * (0.3 + d);
            break;
        case 'mask':
            s.maskOffset = inv * (1 + d) * sign;
            s.masked = true;
            break;
        case 'drop':
            s.ty = -inv * d * env.height * sign;
            break;
        case 'spin':
            s.rotateDeg = -inv * d * 360 * sign;
            s.scale = 1 - inv * 0.5;
            break;
        case 'trackIn':
            s.trackEm = inv * d * 1.2;
            break;
        case 'fade':
        default:
            break;
    }
    const blur = p.blur || (p.kind === 'blur' ? MOTION_KIND_DEFAULTS.blur.blur : 0);
    s.blurPx = Math.max(0, inv) * blur * env.fontPx * 0.6;
    return s;
}
function compose(a, b) {
    return {
        opacity: a.opacity * b.opacity,
        tx: a.tx + b.tx,
        ty: a.ty + b.ty,
        scale: a.scale * b.scale,
        rotateDeg: a.rotateDeg + b.rotateDeg,
        blurPx: a.blurPx + b.blurPx,
        maskOffset: a.maskOffset + b.maskOffset,
        trackEm: a.trackEm + b.trackEm,
        masked: a.masked || b.masked,
    };
}
/**
 * Evaluate one unit's motion state at `nowMs`.
 * @param revealMs absolute time this unit starts its entrance (hook start + delays)
 */
export function unitMotionAt(motion, hook, revealMs, nowMs, frame) {
    if (nowMs < hook.startMs || nowMs >= hook.endMs)
        return { ...STILL, opacity: 0 };
    // Entrance.
    const inLocal = nowMs - revealMs;
    if (inLocal < 0) {
        // Not revealed yet: hidden (a mask unit is also pushed out of its box).
        return { ...STILL, opacity: 0, masked: motion.in.kind === 'mask' };
    }
    const inDur = Math.max(1, motion.in.durationMs);
    const vin = motion.in.kind === 'none' ? 1 : clamp01(inLocal / inDur);
    let s = vin >= 1 && motion.in.kind !== 'shake' ? { ...STILL } : phaseState(motion.in, vin, false, { ...frame, localMs: inLocal });
    if (motion.in.kind === 'mask')
        s.masked = true;
    // Exit (all units leave together).
    const outDur = Math.max(0, motion.out.durationMs);
    const exitStart = hook.endMs - outDur;
    if (motion.out.kind !== 'none' && outDur > 0 && nowMs >= exitStart) {
        const q = clamp01((nowMs - exitStart) / outDur); // 0 → just started leaving
        const phase = motion.out.kind === 'mirror'
            ? { ...motion.in, durationMs: outDur, easing: motion.out.easing, blur: motion.out.blur || motion.in.blur }
            : motion.out;
        const mirror = motion.out.kind === 'mirror';
        const vout = 1 - q;
        const o = phaseState(phase, vout, !mirror, { ...frame, localMs: nowMs - exitStart });
        if (phase.kind === 'mask')
            o.masked = true;
        s = compose(s, o);
    }
    return s;
}
/** Block-level hold motion at `nowMs`: extra translate / scale / rotate. */
export function idleMotionAt(motion, hook, nowMs, frame) {
    const a = clamp01(motion.idleAmount);
    const t = nowMs - hook.startMs;
    const dur = Math.max(1, hook.endMs - hook.startMs);
    const r = { tx: 0, ty: 0, scale: 1, rotateDeg: 0 };
    switch (motion.idle) {
        case 'float':
            r.ty = Math.sin((t / 1800) * Math.PI * 2) * a * frame.height * 0.012;
            break;
        case 'pulse':
            r.scale = 1 + Math.sin((t / 900) * Math.PI * 2) * 0.035 * a;
            break;
        case 'pushIn':
            r.scale = 1 + a * 0.14 * clamp01(t / dur);
            break;
        case 'drift':
            r.tx = (clamp01(t / dur) - 0.5) * a * frame.width * 0.08;
            break;
        case 'sway':
            r.rotateDeg = Math.sin((t / 2200) * Math.PI * 2) * 3 * a;
            break;
        case 'shake': {
            // Deterministic jitter, re-rolled every ~50ms.
            const k = Math.floor(t / 50);
            r.tx = (hash01(k * 2 + 1) - 0.5) * a * frame.height * 0.01;
            r.ty = (hash01(k * 2 + 2) - 0.5) * a * frame.height * 0.01;
            break;
        }
        case 'none':
        default:
            break;
    }
    return r;
}
function hash01(n) {
    const x = Math.sin(n * 12.9898) * 43758.5453;
    return x - Math.floor(x);
}
const P = (kind, durationMs, over = {}) => ({ kind, durationMs, ...MOTION_KIND_DEFAULTS[kind], ...over });
const O = (kind, durationMs, over = {}) => ({
    kind,
    durationMs,
    ...(kind === 'mirror' ? { distance: 0, blur: 0, easing: 'easeOut', fade: true } : MOTION_KIND_DEFAULTS[kind]),
    ...over,
});
const M = (m) => ({
    split: 'block',
    staggerMs: 0,
    idle: 'none',
    idleAmount: 0.5,
    ...m,
});
export const HOOK_MOTION_PRESETS = [
    { id: 'punch', name: 'Punch', desc: 'springs in from small with a bounce', motion: legacyMotion('punch') },
    { id: 'rise', name: 'Rise & fall', desc: 'fades + slides up, then leaves the way it came', motion: M({ in: P('slideUp', 520, { distance: 0.12 }), out: O('mirror', 420) }) },
    { id: 'deep-rise', name: 'Deep rise', desc: 'rises from far below with motion blur', motion: M({ in: P('slideUp', 800, { distance: 0.4, blur: 0.4 }), out: O('mirror', 500) }) },
    { id: 'blur-in', name: 'Blur in', desc: 'focuses in from a soft blur, blurs back out', motion: M({ in: P('blur', 650), out: O('blur', 420) }) },
    { id: 'line-build', name: 'Line build', desc: 'each line rises in on its own beat, then all fade out', motion: M({ split: 'line', staggerMs: 380, in: P('slideUp', 480, { distance: 0.05, blur: 0.5 }), out: O('mirror', 380) }) },
    { id: 'mask-up', name: 'Mask reveal', desc: 'lines rise out from behind an invisible edge', motion: M({ split: 'line', staggerMs: 140, in: P('mask', 620), out: O('mask', 420) }) },
    { id: 'word-pop', name: 'Word by word', desc: 'words pop in one after another', motion: M({ split: 'word', staggerMs: 140, in: P('slideUp', 360, { distance: 0.03, blur: 0.5 }), out: O('fade', 300) }) },
    { id: 'typewriter', name: 'Typewriter', desc: 'letters appear one at a time', motion: M({ split: 'char', staggerMs: 45, in: P('fade', 60, { easing: 'linear' }), out: O('fade', 260) }) },
    { id: 'letter-rise', name: 'Letter cascade', desc: 'letters bounce up in a wave', motion: M({ split: 'char', staggerMs: 32, in: P('slideUp', 420, { distance: 0.04, easing: 'back', blur: 0 }), out: O('fade', 300) }) },
    { id: 'slide-in', name: 'Slide across', desc: 'glides in from the left and exits right', motion: M({ in: P('slideRight', 600), out: O('slideRight', 420) }) },
    { id: 'cinematic', name: 'Cinematic', desc: 'settles in from big + blurred, then slowly pushes in', motion: M({ in: P('zoomOut', 850, { blur: 0.45 }), out: O('fade', 500), idle: 'pushIn', idleAmount: 0.5 }) },
    { id: 'track-in', name: 'Tracking in', desc: 'wide-spaced letters draw together', motion: M({ in: P('trackIn', 900), out: O('fade', 450), idle: 'pushIn', idleAmount: 0.3 }) },
    { id: 'drop', name: 'Drop', desc: 'drops from above and bounces into place', motion: M({ in: P('drop', 700), out: O('slideDown', 380, { distance: 0.08 }) }) },
    { id: 'spin', name: 'Spin in', desc: 'twists in from a tilt', motion: M({ in: P('spin', 600), out: O('zoomIn', 320) }) },
    { id: 'float', name: 'Float', desc: 'fades in and gently floats while it holds', motion: M({ in: P('fade', 450), out: O('fade', 400), idle: 'float', idleAmount: 0.7 }) },
];
export const HOOK_MOTION_PRESETS_BY_ID = Object.fromEntries(HOOK_MOTION_PRESETS.map((p) => [p.id, p]));
//# sourceMappingURL=hook-motion.js.map